import React, { useCallback, useMemo } from 'react';
import { cn } from '../ui/cn.js';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function colorClassForIndex(i, n) {
  const palette = [
    'bg-[#c7e8d9]',
    'bg-[#b9e6e5]',
    'bg-[#b7d7f6]',
    'bg-[#a9c4ff]',
    'bg-[#b9b2ff]',
    'bg-[#d3b8ff]',
    'bg-[#efc0e6]',
    'bg-[#f3c8d2]'
  ];

  if (n <= 1) return palette[0];
  const idx = Math.round((i / (n - 1)) * (palette.length - 1));
  return palette[clamp(idx, 0, palette.length - 1)];
}

export default function StepSlider({
  label,
  value,
  steps,
  onChange,
  leftHint = '',
  rightHint = '',
  density = 'default',
  disabled = false,
  fallbackLabel = null
}) {
  const compact = density === 'compact';
  const idx = useMemo(() => steps.findIndex((s) => s.value === value), [steps, value]);
  const selected = idx >= 0 ? steps[idx] : null;

  const setByIndex = useCallback(
    (nextIndex) => {
      const safe = clamp(nextIndex, 0, steps.length - 1);
      onChange(steps[safe].value);
    },
    [onChange, steps]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setByIndex((idx >= 0 ? idx : 0) - 1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setByIndex((idx >= 0 ? idx : 0) + 1);
      }
    },
    [disabled, idx, setByIndex]
  );

  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      <div className={`${compact ? 'text-[13px]' : 'text-sm'} font-semibold text-ptcm-ink dark:text-gray-100`}>
        {label}
      </div>

      <div
        className={cn(
          'rounded-[24px] border border-ptcm-border dark:border-gray-800 bg-white/90 dark:bg-gray-900',
          compact ? 'p-2' : 'p-3',
          disabled ? 'opacity-60' : null
        )}
        role="radiogroup"
        aria-label={label}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={onKeyDown}
      >
        <div className={`flex items-center justify-between ${compact ? 'text-[10px]' : 'text-[11px]'} text-ptcm-muted dark:text-gray-400`}>
          <span>{leftHint}</span>
          <span>{rightHint}</span>
        </div>

        <div className={`relative ${compact ? 'mt-1.5' : 'mt-2'}`}>
          <div className="h-2 rounded-full bg-ptcm-panel dark:bg-gray-800" />
          {idx >= 0 ? (
            <div
              className={cn(
                'absolute inset-y-0 left-0 h-2 rounded-full',
                'transition-all',
                // fill with the selected color, not a gradient: more legible
                colorClassForIndex(idx, steps.length)
              )}
              style={{ width: `${(idx / (steps.length - 1)) * 100}%` }}
            />
          ) : null}

          <div className="absolute inset-0 flex items-center justify-between">
            {steps.map((s, i) => {
              const isSelected = i === idx;
              const dotColor = colorClassForIndex(i, steps.length);
              return (
                <button
                  key={s.value}
                  type="button"
                  disabled={disabled}
                  className={cn(
                    compact ? 'h-7 w-7' : 'h-8 w-8',
                    'rounded-full flex items-center justify-center',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ptcm-primary focus-visible:ring-offset-2',
                    'ring-offset-ptcm-bg dark:ring-offset-gray-900',
                    disabled ? 'cursor-not-allowed' : null
                  )}
                  aria-label={s.label}
                  aria-checked={isSelected}
                  role="radio"
                  onClick={() => {
                    if (disabled) return;
                    onChange(s.value);
                  }}
                >
                  <span
                    className={cn(
                      compact ? 'h-3 w-3' : 'h-3.5 w-3.5',
                      'rounded-full border',
                      'transition-transform',
                      isSelected ? 'scale-110 border-transparent' : 'border-ptcm-border dark:border-gray-700',
                      isSelected ? dotColor : 'bg-white dark:bg-gray-900'
                    )}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <div className={`${compact ? 'mt-1 text-[13px]' : 'mt-2 text-sm'} text-ptcm-ink dark:text-gray-200 min-h-[1.25rem]`}>
          {selected ? selected.label : (
            fallbackLabel
              ? <span className="text-ptcm-muted dark:text-gray-400">{fallbackLabel}</span>
              : <span className="text-ptcm-muted dark:text-gray-400">Choisir une option</span>
          )}
        </div>
      </div>
    </div>
  );
}

